import React, { useEffect, useState } from "react";
import { Alert, Button, Group, Modal, Paper, Stack, Tabs, Text, Title } from "@mantine/core";
import { IProduct, ManagementConfig, ProductConfig } from "types/productManagementTypes";
import {
  DisplaySection,
  KioskSection,
  PowerTimeoutsSection,
  SystemSettingsSection,
} from "./ManagementConfigForm";
import { deviceStateToManagementConfig, hasReportedState } from "./deviceStateConfig";
import ConfigOverviewPanel from "./ConfigOverviewPanel";
import PlayerPrefsEditor from "./PlayerPrefsEditor";
import DeviceStatesPanel, { ProductFetchStatus } from "./DeviceStatesPanel";

export type ConfigTabValue = "config" | "settings" | "keys";

// Rendered inside the parent's <Tabs.List> so both surfaces share one tab bar.
export const ProductConfigTabList: React.FC<{ hasPid: boolean }> = ({ hasPid }) => (
  <>
    <Tabs.Tab value="config">Config</Tabs.Tab>
    <Tabs.Tab value="settings" disabled={!hasPid}>
      Device settings
    </Tabs.Tab>
    <Tabs.Tab value="keys" disabled={!hasPid}>
      Device keys
    </Tabs.Tab>
  </>
);

interface ProductConfigPanelsProps {
  config: ProductConfig;
  product: IProduct | null;
  productStatus: ProductFetchStatus;
  submitting: boolean;
  onPush: (config: ManagementConfig) => void;
  onPlayerPref: (key: string, value: string) => void;
  onCancel?: () => void;
  onReset?: (pid: string) => void;
  hasSavedConfig?: boolean;
  otherEntries?: ProductConfig[] | null;
  otherEntriesFailed?: boolean;
}

const ProductConfigPanels: React.FC<ProductConfigPanelsProps> = ({
  config,
  product,
  productStatus,
  submitting,
  onPush,
  onPlayerPref,
  onCancel,
  onReset,
  hasSavedConfig = true,
  otherEntries,
  otherEntriesFailed,
}) => {
  const [draftConfig, setDraftConfig] = useState<ManagementConfig>(() =>
    deviceStateToManagementConfig(product, config.config),
  );
  const [confirmReset, setConfirmReset] = useState(false);

  useEffect(() => {
    setDraftConfig(deviceStateToManagementConfig(product, config.config));
  }, [config, product]);

  const unappliedNotice = hasSavedConfig && config.applied === false && (
    <Alert color="yellow" variant="light" title="A config is already queued for this device">
      A config was saved on{" "}
      {config.date ? new Date(config.date).toLocaleString() : "an unknown date"} and the device has
      not confirmed it yet. Saving again replaces that queued config; it reaches the headset the
      next time it connects.
    </Alert>
  );

  const resetSection = onReset && config.pid && (
    <Paper withBorder radius="sm" p="md">
      <Stack gap="xs">
        <Stack gap={2}>
          <Text fw={600} size="sm">
            Reset config
          </Text>
          <Text size="xs" c="dimmed">
            Clears the config saved here for this device and tells the headset to go back to
            defaults, which means the default config matching its versions.
          </Text>
        </Stack>
        <div>
          <Button
            color="red"
            variant="light"
            disabled={submitting}
            onClick={() => setConfirmReset(true)}
            data-testid="product-status-reset-config"
          >
            Reset config
          </Button>
        </div>
      </Stack>
    </Paper>
  );

  const resetModal = onReset && config.pid && (
    <Modal
      opened={confirmReset}
      onClose={() => (submitting ? null : setConfirmReset(false))}
      title="Reset device config?"
      centered
    >
      <Stack gap="md">
        <Text size="sm">
          This will clear <strong>{config.pid}</strong>&apos;s saved config and tell the device to
          return to defaults.
        </Text>
        <Group justify="flex-end">
          <Button variant="default" onClick={() => setConfirmReset(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            color="red"
            loading={submitting}
            onClick={() => {
              onReset(config.pid as string);
              setConfirmReset(false);
            }}
          >
            Reset
          </Button>
        </Group>
      </Stack>
    </Modal>
  );

  const pushFooter = (
    <Group justify="flex-end">
      {onCancel && (
        <Button variant="default" onClick={onCancel} disabled={submitting}>
          Cancel
        </Button>
      )}
      <Button onClick={() => onPush(draftConfig)} loading={submitting}>
        Save &amp; push to device
      </Button>
    </Group>
  );

  return (
    <>
      <Tabs.Panel value="config" pt="md">
        <ConfigOverviewPanel
          config={config}
          product={product}
          productStatus={productStatus}
          hasSavedConfig={hasSavedConfig}
          otherEntries={otherEntries}
          otherEntriesFailed={otherEntriesFailed}
        />
      </Tabs.Panel>

      <Tabs.Panel value="settings" pt="md">
        <Stack gap="lg">
          {config.pid && <DeviceStatesPanel product={product} status={productStatus} />}
          <Stack gap={4}>
            <Title order={5}>Set the Device config</Title>
            <Text size="xs" c="dimmed">
              {hasReportedState(product)
                ? "The fields below start from what the headset last reported, then the config stored on the device, then the config saved here."
                : "The device has not reported its state, so the fields below start from the config saved here."}
            </Text>
          </Stack>
          {unappliedNotice}
          <Paper withBorder radius="sm" p="md">
            <DisplaySection value={draftConfig} onChange={setDraftConfig} disabled={submitting} />
          </Paper>
          <Paper withBorder radius="sm" p="md">
            <SystemSettingsSection
              value={draftConfig}
              onChange={setDraftConfig}
              disabled={submitting}
            />
          </Paper>
          <Paper withBorder radius="sm" p="md">
            <PowerTimeoutsSection
              value={draftConfig}
              onChange={setDraftConfig}
              disabled={submitting}
            />
          </Paper>
          <Paper withBorder radius="sm" p="md">
            <KioskSection value={draftConfig} onChange={setDraftConfig} disabled={submitting} />
          </Paper>
          {pushFooter}
          {resetSection}
          {resetModal}
        </Stack>
      </Tabs.Panel>

      <Tabs.Panel value="keys" pt="md">
        <PlayerPrefsEditor onSubmit={onPlayerPref} submitting={submitting} product={product} />
      </Tabs.Panel>
    </>
  );
};

export default ProductConfigPanels;
